import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from '@radix-ui/react-dialog';
import {
  StyledDialogAnimation,
  StyledDialogOver,
  StyledSpinner,
  contentShow,
  fadeOut,
} from '@tryhackme/thm-ui-components';
import styled from 'styled-components';

export const StyledModalContent = styled.div`
  margin-bottom: 1.6rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2.4rem;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    grid-template-columns: 1fr;
  }
`;

export const StyledLearningPathOverview = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  letter-spacing: 0.14rem;
  margin-bottom: 0.6rem;
`;

export const StyledPathTitle = styled(Dialog.Title)`
  font-size: 2.4rem;
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  margin: 0;
`;

export const StyledDescriptionAndCertificateColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding: 0 0.5rem;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    position: static;
  }
`;

export const StyledPathContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledPathIntro = styled.div`
  margin-bottom: 1rem;

  h3 {
    font-size: 2.4rem;
    font-weight: ${({ theme }) => theme.fonts.weight.regular};
    margin-bottom: 1.6rem;
  }
  p,
  li,
  div,
  span {
    color: ${({ theme }) => theme.colors.text.lighter};
    font-size: ${({ theme }) => theme.fonts.sizes.default} !important;
    line-height: 2rem;
  }
  p {
    text-align: left !important;
    margin-bottom: 1rem;
  }
  ul,
  ol {
    padding-left: 2.4rem;
    margin-bottom: 1rem;
  }
  ul {
    list-style: disc;
  }
`;

export const StyledPathCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.small};
  box-shadow: ${({ theme }) => theme.boxShadow.light};
  background-color: ${({ theme }) => theme.colors.background.select};
`;

export const StyledPathCardBanner = styled.img`
  max-width: 27.4rem;
  max-height: 15.6rem;
  object-fit: cover;
  object-position: center top;
  border-radius: 0.5rem 0.5rem 0 0;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    max-width: 100%;
  }
`;

export const StyledPathCardContent = styled.div`
  padding: 2.4rem;
  p,
  li,
  div,
  span {
    color: ${({ theme }) => theme.colors.text.lighter};
    font-size: ${({ theme }) => theme.fonts.sizes.small};
    line-height: 2rem;
  }
  p {
    text-align: left !important;
  }

  ul {
    padding-left: 2.4rem;
    list-style: disc;
    margin-top: 0.8rem;
  }
`;

export const StyledPathSectionItem = styled.div<{ $isScrollbarVisible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border.dropdown};
  padding: ${({ $isScrollbarVisible }) => ($isScrollbarVisible ? '0.8rem 1.6rem 0.8rem 0' : '0.8rem 0')};
`;

export const StyledPathSectionItemImageAndTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  img {
    max-width: 3.6rem;
  }
`;

export const StyledCompletedRoomIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.success.main};
  width: 2rem;
  height: 2rem;
`;

export const ProgressBarWrapper = styled.div`
  width: 6.5rem;
`;

export const ProgressBarAndLabelWrapper = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`;

export const ProgressBarLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`;

export const StyledPlayIcon = styled(FontAwesomeIcon)`
  && {
    width: 1.2rem;
    height: 1.8rem;
  }
`;

export const StyledPathSection = styled.div<{ isDarkMode: boolean }>`
  :not(:last-child) {
    margin-bottom: 2.4rem;
  }

  h4 {
    font-weight: ${({ theme }) => theme.fonts.weight.bold};
    color: ${({ theme, isDarkMode }) => (isDarkMode ? theme.colors.grey[500] : theme.colors.grey[600])};
    font-size: ${({ theme }) => theme.fonts.sizes.default};
    margin-bottom: 0.8rem;
  }
  p {
    font-weight: ${({ theme }) => theme.fonts.weight.bold};
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }
`;

export const StyledCertificateImage = styled.img`
  max-width: 27.4rem;
  max-height: 19.3rem;
  object-fit: contain;
  object-position: center;
  border-radius: ${({ theme }) => `${theme.radius.small} ${theme.radius.small} 0 0`};

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    max-width: 100%;
  }
`;

export const StyledDialogContentWrapper = styled.div`
  --header-size: 8rem;
  --footer-size: 4.6rem;
  --padding-size: 6.4rem;
  max-height: calc(79vh - var(--header-size) - var(--footer-size) - var(--padding-size));
  overflow-y: hidden;
  :hover {
    overflow-y: auto;
  }
`;

export const StyledDialogOverlay = styled(Dialog.Overlay)`
  ${StyledDialogOver};
  ${StyledDialogAnimation};
  background-color: rgba(0, 0, 0, 0.4);
  overflow-y: auto;
  inset: 0;
  padding: 1rem 0;
  z-index: 1000;
`;

export const StyledDialogFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.border.dropdown};
  padding-top: 1.6rem;
`;

export const StyledDialogContent = styled(Dialog.Content)`
  background: ${({ theme }) => theme.colors.background.light};
  border-radius: ${({ theme }) => theme.radius.small};
  box-shadow: ${({ theme }) => theme.boxShadow.light};
  color: ${({ theme }) => theme.colors.text.main};
  width: 95vw;
  max-width: 85.8rem;
  position: relative;
  margin: auto 0;
  overflow-x: hidden;
  max-height: 80vh;
  padding: 3.2rem;

  @media screen and (max-width: ${({ theme }) => theme.sizes.sm}) {
    max-height: 100%;
  }

  &:focus {
    outline: none;
  }

  &[data-state='open'] {
    animation: ${contentShow} 150ms ease-out;
  }

  &[data-state='closed'] {
    animation: ${fadeOut} 150ms ease-out;
  }
`;

export const StyledModalHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 2.4rem;

  h2,
  p {
    margin-bottom: 0;
  }
`;

export const StyledCloseIconButton = styled(Dialog.Close)`
  outline: none;
  position: absolute;
  padding: 0.9rem;
  border: 0.1rem solid transparent;
  border-radius: ${({ theme }) => theme.radius.small};
  top: 1.8rem;
  right: 1.8rem;

  svg {
    color: ${({ theme }) => theme.colors.text.lighter};
    height: 2.4rem;
    width: 2.4rem;
    display: block;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.info.main};
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    top: 0.8rem;
    right: 0.8rem;
  }
`;

export const StyledLoadingSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledLoadingSpinner = styled(StyledSpinner)`
  color: ${({ theme }) => theme.colors.text.lighter};
`;
